import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import {
  AlertTriangle,
  Calendar,
  Crown,
  ExternalLink,
  Gift,
  Gem,
  RotateCw,
  Ticket,
  Trophy,
  Users
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { eventApi } from '../../../services'

const SUPPORT_BOT_LINK = 'https://t.me/skinoz_support_bot'

const EventPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [eventData, setEventData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let isMounted = true

    const loadEvent = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const data = await eventApi.getEventData()

        if (data?.error) {
          const backendErrorText = t('event.ref.errorBackend', {
            defaultValue: 'Ошибка сервера. Попробуйте позже.'
          })
          toast.error(backendErrorText)
          if (isMounted) {
            setError(backendErrorText)
          }
          return
        }

        if (isMounted) {
          setEventData(data || {})
        }
      } catch (e) {
        if (isMounted) {
          setError(
            t('event.ref.errorLoad', {
              defaultValue: 'Не удалось загрузить данные ивента'
            })
          )
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadEvent()

    return () => {
      isMounted = false
    }
  }, [t])

  const topList = useMemo(() => (Array.isArray(eventData?.top) ? eventData.top : []), [eventData])
  const prizeList = useMemo(() => (Array.isArray(eventData?.prize) ? eventData.prize : []), [eventData])
  const userStats = eventData?.user && typeof eventData.user === 'object' ? eventData.user : null
  const hasUserInTop = useMemo(() => topList.some((entry) => Boolean(entry?.you)), [topList])
  const showUserStatsCard = Boolean(userStats && !hasUserInTop)

  const formatPrice = (value) => Number(value || 0).toLocaleString('ru-RU')
  const formatChance = (value) => {
    if (value === null || value === undefined || value === '') return '—'
    const chanceAsText = String(value)
    return chanceAsText.includes('%') ? chanceAsText : `${chanceAsText}%`
  }

  const truncateNickname = (value) => {
    const text = String(value || '').trim()
    if (!text) return '—'
    return text.length > 16 ? `${text.slice(0, 16)}…` : text
  }

  const openSupportLink = () => {
    const tg = window.Telegram?.WebApp
    if (tg?.openLink) {
      tg.openLink(SUPPORT_BOT_LINK)
      return
    }
    window.open(SUPPORT_BOT_LINK, '_blank', 'noopener,noreferrer')
  }

  if (isLoading) {
    return (
      <div className="min-h-screen px-6 pb-10 pt-6 text-white">
        <div className="mx-auto w-full lg:max-w-6xl">
          <div className="rounded-3xl border border-white/10 bg-surface/70 p-6">
            <div className="flex items-center justify-center">
              <div className="h-10 w-10 rounded-full border-2 border-sky-300/30 border-t-sky-300 animate-spin" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen px-6 pb-10 pt-6 text-white">
        <div className="mx-auto w-full lg:max-w-6xl">
          <div className="rounded-3xl border border-red-500/30 bg-red-500/10 p-6 text-center">
            <div className="flex items-center justify-center">
              <AlertTriangle size={28} className="text-red-200" />
            </div>
            <div className="mt-3 text-sm leading-relaxed text-red-100/90">{error}</div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl border border-red-400/40 px-4 py-2 text-sm font-semibold text-red-100"
            >
              <RotateCw size={16} className="text-red-100" />
              {t('event.ref.retry', { defaultValue: 'Повторить' })}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen px-6 pb-10 pt-6 text-white">
      <div className="mx-auto w-full lg:max-w-6xl">
        <div className="lg:grid lg:grid-cols-12 lg:items-start lg:gap-6">
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-indigo-300/20 bg-gradient-to-br from-sky-500/10 via-indigo-500/10 to-purple-500/10 overflow-hidden">
              <div className="relative">
                <img src="/images/event/event-ref.jpg" alt="event" className="w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070912]/90 via-[#070912]/30 to-transparent" />
              </div>

              <div className="p-5 sm:p-6">
                <h1 className="mt-3 text-3xl font-black leading-tight">
                  {t('event.ref.title', { defaultValue: 'Охота на рефералов' })}
                </h1>

                <p className="mt-3 text-base text-white/85 leading-relaxed">
                  {t('event.ref.description', {
                    defaultValue: 'Зови друзей, получай X5 кристаллов за каждого, и шанс выиграть 1 из 5 ножей!'
                  })}
                </p>

                <div className="mt-4 inline-flex items-center gap-2 rounded-xl border border-white/10 bg-dark-primary/45 px-3 py-2 text-sm text-white/85">
                  <Calendar size={14} className="text-purple-200" />
                  {t('event.ref.period', { defaultValue: '27 апреля - 11 мая' })}
                </div>

                <button
                  type="button"
                  onClick={() => navigate('/referrals')}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-sky-200/40 bg-gradient-to-r from-sky-500/30 to-purple-500/30 px-4 py-3 text-sm font-black text-white hover:border-sky-200/70"
                >
                  <Users size={18} className="text-white" />
                  {t('event.ref.ctaInvite', { defaultValue: 'Пригласить друзей' })}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-4 lg:col-span-7 lg:mt-0">
            {!userStats && (
              <div className="rounded-2xl border border-sky-300/20 bg-sky-500/10 p-4 text-sm text-sky-100/90">
                {t('event.ref.notParticipant', {
                  defaultValue: 'Ты пока не участвуешь — пригласи друзей по реферальной ссылке, чтобы получить билеты в розыгрыш.'
                })}
              </div>
            )}

            {showUserStatsCard && (
              <div className="rounded-2xl border border-purple-300/25 bg-purple-500/10 p-4">
                <div className="text-sm font-semibold text-purple-100">
                  {t('event.ref.myStatsTitle', { defaultValue: 'Твоя статистика в ивенте' })}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-white/10 bg-dark-primary/35 p-3">
                    <div className="text-[11px] uppercase tracking-[0.1em] text-white/55">
                      {t('event.ref.qualifiedReferrals', { defaultValue: 'Квалифицированные рефералы' })}
                    </div>
                    <div className="mt-1 text-lg font-black text-white">{Number(userStats?.l1_count || 0)}</div>
                  </div>

                  <div className="rounded-xl border border-white/10 bg-dark-primary/35 p-3">
                    <div className="text-[11px] uppercase tracking-[0.1em] text-white/55">
                      {t('event.ref.currentChance', { defaultValue: 'Текущий шанс' })}
                    </div>
                    <div className="mt-1 text-lg font-black text-white">{formatChance(userStats?.chance)}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-white/10 bg-surface/70 p-4 sm:p-5">
              <div className="flex items-center gap-2">
                <Trophy size={18} className="text-amber-200" />
                <h2 className="text-lg font-black">{t('event.ref.topTitle', { defaultValue: 'Топ участников' })}</h2>
              </div>

              {topList.length === 0 ? (
                <div className="mt-5 flex items-center justify-center py-6">
                  <Users size={36} className="text-white/35" />
                </div>
              ) : (
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-2">
                  {topList.map((entry, index) => {
                    const position = Number(entry?.pos ?? index + 1)
                    const isCurrentUser = Boolean(entry?.you)

                    return (
                      <div
                        key={`${position}-${entry?.name || 'user'}`}
                        className={`rounded-xl border p-3.5 ${
                          isCurrentUser
                            ? 'border-sky-300/50 bg-gradient-to-r from-sky-500/20 to-purple-500/20'
                            : 'border-white/10 bg-dark-primary/40'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white/90">
                            {position}
                          </div>

                          <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-white/10 bg-dark-primary">
                            <img src={entry?.ava} alt={entry?.name || 'User'} className="h-full w-full object-cover" />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold text-white">{truncateNickname(entry?.name)}</div>
                            <div className="mt-0.5 text-xs text-white/65">
                              {t('event.ref.chanceLabel', { defaultValue: 'Шанс' })}: {formatChance(entry?.chance)}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-white/10 bg-surface/70 p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <Gift size={18} className="text-purple-200" />
            <h2 className="text-lg font-black">{t('event.ref.prizesTitle', { defaultValue: 'Призы розыгрыша' })}</h2>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {prizeList.map((prize, index) => (
              <div key={`${prize?.name || 'prize'}-${index}`} className="rounded-xl border border-white/10 bg-dark-primary/45 p-3">
                <div className="flex justify-center rounded-lg bg-black/20 p-2">
                  <img
                    src={prize?.img}
                    alt={prize?.name || 'prize'}
                    className="h-[130px] w-[130px] rounded-lg object-contain"
                  />
                </div>
                <div className="mt-2 text-sm font-semibold text-white break-words">{prize?.name}</div>
                <div className="mt-1 inline-flex items-center gap-1 text-xs text-white/70">
                  <Gem size={12} className="text-white/60" />
                  {formatPrice(prize?.price)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-surface/70 p-5">
            <div className="flex items-center gap-2">
              <Gift size={18} className="text-sky-200" />
              <h3 className="text-base font-black">{t('event.ref.whatPrizeTitle', { defaultValue: 'Что разыгрываем' })}</h3>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-white/80 leading-relaxed list-disc pl-5">
              <li>{t('event.ref.prize1', { defaultValue: '5000 кристаллов за каждого квалифицированного реферала.' })}</li>
              <li>
                {t('event.ref.prize2', {
                  defaultValue:
                    '+1000 кристаллов за каждого квалифицированного реферала, если твоя реферальная ссылка размещена публично и одобрена проверкой SKINOZ.'
                })}
              </li>
              <li>
                {t('event.ref.prize3', {
                  defaultValue:
                    'Размещать ссылку можно в Telegram-каналах, Instagram, TikTok и YouTube (минимум 50 подписчиков).'
                })}
              </li>
              <li>
                {t('event.ref.prize4', {
                  defaultValue: 'Чтобы получить повышенную награду, отправь ссылку на проверку в саппорт.'
                })}
              </li>
              <li>{t('event.ref.prize5', { defaultValue: 'Не убирай ссылку до конца ивента.' })}</li>
              <li>
                {t('event.ref.prize6', {
                  defaultValue: '5 ножей разыграем среди всех, кто пригласил хотя бы 1 квалифицированного реферала.'
                })}
              </li>
            </ul>

            <button
              type="button"
              onClick={openSupportLink}
              className="mt-4 inline-flex items-center gap-2 rounded-xl border border-sky-300/35 bg-sky-500/15 px-3 py-2 text-xs font-semibold text-sky-100 hover:border-sky-300/60"
            >
              <ExternalLink size={14} />
              {t('event.ref.supportCta', { defaultValue: 'Отправить ссылку в саппорт' })}
            </button>
          </div>

          <div className="rounded-2xl border border-white/10 bg-surface/70 p-5">
            <div className="flex items-center gap-2">
              <Users size={18} className="text-indigo-200" />
              <h3 className="text-base font-black">{t('event.ref.howToTitle', { defaultValue: 'Как принять участие' })}</h3>
            </div>
            <p className="mt-3 text-sm text-white/80 leading-relaxed">
              {t('event.ref.howToText', {
                defaultValue:
                  'Пригласи друзей по своей реферальной ссылке. За каждого квалифицированного реферала ты получаешь 5000 кристаллов, а также 1 билет в розыгрыш 5 ножей. Если твоя ссылка размещена в публичном профиле, канале или соцсети и прошла проверку, награда увеличивается до 6000 кристаллов за каждого квалифицированного реферала.'
              })}
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-surface/70 p-5">
            <div className="flex items-center gap-2">
              <Ticket size={18} className="text-amber-200" />
              <h3 className="text-base font-black">{t('event.ref.rulesTitle', { defaultValue: 'Условия выбора победителя' })}</h3>
            </div>
            <ul className="mt-3 space-y-2 text-sm text-white/80 leading-relaxed list-disc pl-5">
              <li>
                {t('event.ref.rule1', {
                  defaultValue:
                    'За каждого квалифицированного реферала начисляется 1 билет в розыгрыш. Чем больше билетов, тем выше шанс получить 1 из 5 ножей.'
                })}
              </li>
              <li>
                {t('event.ref.rule2', {
                  defaultValue:
                    'Реферал считается квалифицированным, если он сделал хотя бы 1 депозит в период ивента или сделал хотябы 1 вывод скина в Steam (бесплатный стартовый скин тоже считается).'
                })}
              </li>
              <li>
                {t('event.ref.rule3', {
                  defaultValue:
                    'Фрод, мультиаккаунты, боты, мотивированные регистрации и любые попытки накрутки не засчитываются. SKINOZ оставляет за собой право проверить трафик и заморозить награды до окончания проверки.'
                })}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-sky-300/25 bg-gradient-to-r from-sky-500/15 to-purple-500/15 p-5 text-center">
          <div className="text-base font-semibold text-white/90">
            {t('event.ref.bottomCtaTitle', {
              defaultValue: 'Готов участвовать? Зови друзей и увеличивай шанс на победу!'
            })}
          </div>

          <button
            type="button"
            onClick={() => navigate('/referrals')}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-sky-200/40 bg-gradient-to-r from-sky-500/30 to-purple-500/30 px-4 py-3 text-sm font-black text-white hover:border-sky-200/70"
          >
            <Users size={18} className="text-white" />
            {t('event.ref.ctaInvite', { defaultValue: 'Пригласить друзей' })}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EventPage
